import { useEffect, useRef, useState } from "react";
import axios from "axios";
import nodataimage from "@assets/images/common/no-data.png";
import s from "../../../src/components/templates/Profile/ProfileTable/ProfileTable.module.scss";
import { useDispatch, useSelector } from "react-redux";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import { cachingPanelData, companyCategoryData, profileTypeEndPoint, profileTypeEnum } from "@containers/CompanyProfile/utils/enum.js";
import Country from "./components/Country";
import Locations from "./components/Locations";
import Ports from "./components/Ports";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { buyerCachingHandler, supplierCachingHandler } from "@redux/CompanyProfile/companyProfileSlice";
import { getTitleCase } from "@utils/utils";
import { toast } from "react-toastify";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";

const TradeOverview = ({ loader, setLoader, pathname, userActivePlan, categoryData, activePanelId, activeCategory, profileData, countDetails }) => {
  const { buyerCaching, supplierCaching, isParentSubs } = useSelector((state) => state["companyProfile"]);
  const [expandedItems, setExpandedItems] = useState(["1-importers-trade_country"]);
  const { companyID, companyname } = useParams();
  const dispatch = useDispatch();
  const source = useRef(null);
  const companyName = getTitleCase(buyerCaching?.profileDetails?.name) ?? "";

  const activeCategData = categoryData.find((cat) => cat.id === activeCategory);
  const subCategoryMustNot = activePanelId === cachingPanelData.importers ? buyerCaching[`${activeCategory}_must_not`] : activePanelId === cachingPanelData.exporters ? supplierCaching[`${activeCategory}_must_not`] : [];
  const { userDetails } = useSelector((state) => state);
  const profileType = userDetails?.userData?.industry;
  const query = new URLSearchParams(window.location.search);
  const mergedID = query.get("mergedID");
  const country = query.get("country")?.toLowerCase();
  const countryType = query.get("type");
  const dataSource = country + "-" + countryType;
  const [lastPage, setLastPage] = useState(1);
  useEffect(() => {
    setLastPage(1);
  }, [activeCategory]);

  const isLogisticsProfile = profileType === "logistics provider";

  //handlepagination api call
  const fetchTradeOverview = async (lastPage) => {
    try {
      setLoader(true);
      const requestPayload = {
        profile_type: profileTypeEnum.company,
        _id: companyID,
        slug: companyname,
        category: activePanelId,
        map_type: activeCategory,
        sub_category: "overview",
        isSubsidiaryIncluded: isParentSubs,
        must_not: subCategoryMustNot,
      };
      if (mergedID) {
        requestPayload.mergedID = +mergedID;
        requestPayload.isMerged = true;
      }

      if (source.current) {
        source.current.cancel("");
      }
      const CancelToken = axios.CancelToken;
      source.current = CancelToken.source();
      setLastPage(lastPage + 1);
      axios
        .post(profileTypeEndPoint.ports.getMapData, requestPayload, {
          cancelToken: source.current.token,
        })
        .then((res) => {
          const { data, status } = res.data;
          if (status?.toUpperCase() === "SUCCESS" && data) {
            if (activePanelId === cachingPanelData.importers) {
              let buyerCachingClone = JSON.parse(JSON.stringify(buyerCaching));
              buyerCachingClone[activeCategory] = [...buyerCaching[activeCategory], ...data[activeCategory]];
              buyerCachingClone[`${activeCategory}_must_not`] = data.must_not;
              dispatch(buyerCachingHandler(buyerCachingClone));
            } else if (activePanelId === cachingPanelData.exporters) {
              let supplierCachingClone = JSON.parse(JSON.stringify(supplierCaching));
              supplierCachingClone[activeCategory] = [...supplierCaching[activeCategory], ...data[activeCategory]];
              supplierCachingClone[`${activeCategory}_must_not`] = data.must_not;
              dispatch(supplierCachingHandler(supplierCachingClone));
            }
          } else {
            throw new Error("Invalid response status");
          }
        });
    } catch (error) {
      toast.error("Something went wrong. Please try again later!");
    } finally {
      //   setTimeout(() => {
      setLoader(false);
      //   }, 2000);
    }
  };

  const toggleExpand = (serialNo) => {
    if (activeCategory === companyCategoryData[0].id) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewTabTradeCountriesExpand, generateTrackingObject(dataSource, pathname, userActivePlan));
    } else if (activeCategory === companyCategoryData[1].id) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewTabTradePortsExpand, generateTrackingObject(dataSource, pathname, userActivePlan));
    }
    setExpandedItems((prev) => {
      if (prev.includes(serialNo)) {
        return prev.filter((item) => item !== serialNo);
      }
      return [...prev, serialNo];
    });
  };

  const renderListComponent = (activeCategory, profileData) => {
    switch (activeCategory) {
      case companyCategoryData[0].id:
        return <Country activeCategData={activeCategData} companyName={companyName} pathname={pathname} dataSource={dataSource} userActivePlan={userActivePlan} lastPage={lastPage} loader={loader} fetchTradeOverview={fetchTradeOverview} profileData={profileData} activeCategory={activeCategory} expandedItems={expandedItems} activePanelId={activePanelId} headings={headings} toggleExpand={toggleExpand} />;
      case companyCategoryData[1].id:
        return <Ports activeCategData={activeCategData} companyName={companyName} pathname={pathname} dataSource={dataSource} userActivePlan={userActivePlan} lastPage={lastPage} loader={loader} fetchTradeOverview={fetchTradeOverview} isLogisticsProfile={isLogisticsProfile} profileData={profileData} activeCategory={activeCategory} expandedItems={expandedItems} activePanelId={activePanelId} headings={headings} toggleExpand={toggleExpand} />;
      case companyCategoryData[2].id:
        return <Locations activeCategData={activeCategData} companyName={companyName} pathname={pathname} dataSource={dataSource} userActivePlan={userActivePlan} lastPage={lastPage} setLoader={setLoader} loader={loader} fetchTradeOverview={fetchTradeOverview} isLogisticsProfile={isLogisticsProfile} profileData={profileData} activeCategory={activeCategory} expandedItems={expandedItems} activePanelId={activePanelId} headings={headings} toggleExpand={toggleExpand} />;
      default:
        break;
    }
  };

  const headings = [
    {
      id: "trade_country",
      activePanelId: "importers",
      heading1: "Shipments imported",
      heading2: "Top Suppliers from",
      heading3: "Top HS Codes",
      heading3Alt: "Top Ports of Lading",
      redirectprofileType: "portOfLading",
      redirectionType: "ports",
    },
    {
      id: "trade_country",
      activePanelId: "exporters",
      heading1: "Shipments exported",
      heading2: "Top Buyers from",
      heading3: "Top HS Codes",
      heading3Alt: "Top Ports of Unlading",
      redirectprofileType: "portOfUnlading",
      redirectionType: "ports",
    },
    {
      id: "location",
      activePanelId: "importers",
      heading1: "Consignee Address",
      heading2: "Number of Shipments",
      heading3: "",
      heading3Alt: "",
    },
    {
      id: "location",
      activePanelId: "exporters",
      heading1: "Shipper Address",
      heading2: "Number of Shipments",
      heading3: "",
      heading3Alt: "",
    },
    {
      id: "port",
      activePanelId: "importers",
      headingL: "Port of Lading",
      headingR: "Port of Unlading",
      heading2: "Shipments Imported",
      heading3: "Total Port of Unlading",
      heading3Alt: "Total Port of Unlading",
    },
    {
      id: "port",
      activePanelId: "exporters",
      headingL: "Port of Unlading",
      headingR: "Port of Lading",
      heading2: "Shipments Exported",
      heading3: "Total Port of Lading",
      heading3Alt: "Total Port of Lading",
    },
  ];

  return (
    <>
      {companyCategoryData.some((tabData) => countDetails[tabData.countEnum] > 0) ? (
        <div className={`tw-text-sm tw-flex`}>
          {/* {categoryData && categoryData?.length > 0 ? (
            <div className={`${styles.categories} ${loader && activeCategory === companyCategoryData[0].id ? "" : "tw-border-r"} tw-w-fit tw-gap-2 tw-p-4`}>
              {categoryData.map((category) =>
                category?.countEnum ? (
                  countDetails[category?.countEnum] ? (
                    <div
                      key={category.id}
                      className={`tw-flex tw-items-center tw-p-2 tw-h-9 tw-justify-between tw-text-sm tw-font-medium tw-cursor-pointer ${activeCategory === category.id ? "tw-bg-primary-purple-50 tw-text-primary-purple-600 tw-border tw-border-white tw-rounded-lg" : ""}`}
                      onClick={() => {
                        handleCategoryClick(category);
                        setLastPage(1);
                      }}>
                      <div className="tw-flex tw-items-center">
                        <span className="material-symbols-outlined tw-w-5">{category.iconEnum}</span>
                        <span htmlFor={category.id}>{category.value}</span>
                      </div>
                      <span htmlFor={category.id} className={`${activeCategory === category.id ? "tw-text-white tw-bg-primary-purple-500" : ""} tw-bg-gray-200 tw-text-gray-900 tw-font-medium tw-text-xs tw-px-[6px] tw-py-[3px] tw-rounded tw-ml-1`}>
                        {displayCount(countDetails[category?.countEnum], 2)}
                      </span>
                    </div>
                  ) : null
                ) : (
                  !(category.id === portsCategoryData[0].id && !showTradeLane) && (
                    <div key={category.id} className={`${styles.category} ${activeCategory === category.id && styles.activeCategory}`} onClick={() => handleCategoryClick(category)}>
                      <span className="material-symbols-outlined tw-w-5">{category.iconEnum}</span>
                      <span htmlFor={category.id}>{category.value}</span>
                    </div>
                  )
                )
              )}
            </div>
          ) : (
            <></>
          )} */}
          <div className="tw-flex tw-flex-col tw-py-4 tw-pr-4 tw-pl-3 tw-gap-2 tw-w-full">
            <div className="tw-flex tw-flex-col tw-gap-2">
              {loader ? (
                <div className="tw-pl-3">
                  <LoadingSkeleton lines={8} lineHeight="35px" width="100%" shape="rectangle" />
                </div>
              ) : (
                renderListComponent(activeCategory, profileData)
              )}
            </div>
          </div>
        </div>
      ) : loader ? (
        <LoadingSkeleton lines={8} lineHeight="35px" width="100%" shape="rectangle" />
      ) : (
        <div className="d-block text-center w-100 p-5">
          <div className={`card px-2 pt-4 pb-1 w-25 m-auto ${s.noDataCard} border-0`}>
            <div className="text-center card-body p-2">
              <div className="header-image">
                <img className="img-fluid" src={nodataimage} alt="..." />
              </div>
              <p className="mt-4">No Trade Overview data found.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TradeOverview;
